/**
 * RBAC v2 фаза (d) — TanStack Query hooks поверх /api/v1/roles (roles.py).
 *
 * Endpoints (все company_admin-only на бэке):
 *   GET    /roles                    — список: system_roles + custom_roles
 *   GET    /roles/{id}               — детально с матрицей resource×CRUD
 *   POST   /roles                    — создать кастомную
 *   POST   /roles/from-preset        — из пресета (Агрохолдинг/Колхоз)
 *   POST   /roles/duplicate-system   — дублировать системную
 *   PATCH  /roles/{id}               — имя/описание/scope + матрица
 *   DELETE /roles/{id}               — 409 если есть участники
 *   GET    /roles/{id}/members       — диф-превью (member_count)
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  MemberRoleDiff,
  PermissionRow,
  RoleDetail,
  RolesList,
} from '@/pages/company/roles/rolesLogic';

export const rolesKeys = {
  list: () => ['roles', 'list'] as const,
  detail: (id: string) => ['roles', 'detail', id] as const,
  members: (id: string) => ['roles', 'members', id] as const,
};

export function useRoles(): UseQueryResult<RolesList, Error> {
  return useQuery({
    queryKey: rolesKeys.list(),
    queryFn: () => api.get<RolesList>('/api/v1/roles'),
    staleTime: 30_000,
  });
}

export function useRoleDetail(
  id: string | null,
): UseQueryResult<RoleDetail, Error> {
  return useQuery({
    queryKey: rolesKeys.detail(id ?? '—'),
    queryFn: () => api.get<RoleDetail>(`/api/v1/roles/${id}`),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useRoleMembers(
  id: string | null,
): UseQueryResult<MemberRoleDiff, Error> {
  return useQuery({
    queryKey: rolesKeys.members(id ?? '—'),
    queryFn: () => api.get<MemberRoleDiff>(`/api/v1/roles/${id}/members`),
    enabled: !!id,
    staleTime: 10_000,
  });
}

export interface CreateRolePayload {
  code: string;
  name: string;
  description?: string | null;
  scope_kinds?: string[];
  permissions: PermissionRow[];
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRolePayload) =>
      api.post<RoleDetail>('/api/v1/roles', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: rolesKeys.list() });
    },
  });
}

export interface FromPresetPayload {
  preset_code: string;
  preset_role_code: string;
  code?: string | null;
  name?: string | null;
}

export function useCreateRoleFromPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: FromPresetPayload) =>
      api.post<RoleDetail>('/api/v1/roles/from-preset', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: rolesKeys.list() });
    },
  });
}

export interface DuplicateSystemPayload {
  system_code: string;
  code: string;
  name: string;
  description?: string | null;
}

export function useDuplicateSystemRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: DuplicateSystemPayload) =>
      api.post<RoleDetail>('/api/v1/roles/duplicate-system', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: rolesKeys.list() });
    },
  });
}

export interface UpdateRolePayload {
  name?: string | null;
  description?: string | null;
  scope_kinds?: string[] | null;
  permissions?: PermissionRow[] | null;
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRolePayload }) =>
      api.patch<RoleDetail>(`/api/v1/roles/${id}`, payload),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: rolesKeys.list() });
      void qc.invalidateQueries({ queryKey: rolesKeys.detail(vars.id) });
    },
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/roles/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: rolesKeys.list() });
    },
  });
}
